/**
 * usePlanApproval Hook
 *
 * Provides approve/reject/bulk-approve mutations for shadow batches
 * in the Planner view. Calls the approve_shadow_batch() and
 * reject_shadow_batch() RPC functions.
 *
 * US-C2-005: Planner Data Layer
 */

import { useState, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Hook for planner batch approval actions
 *
 * @returns {Object} {
 *   approveBatch: Function(batchId) → Promise<JSONB>,
 *   rejectBatch: Function(batchId) → Promise<void>,
 *   bulkApprove: Function(batchIds) → Promise<Array<JSONB>>,
 *   approving: boolean
 * }
 */
export function usePlanApproval() {
  const [approving, setApproving] = useState(false)

  /**
   * Approve a single shadow batch
   * Converts it to a real batch and generates task instances.
   *
   * @param {string} batchId - UUID of the shadow batch
   * @returns {Promise<Object>} Result with batch_id, batch_code, approved_at, tasks_generated
   */
  const approveBatch = useCallback(async (batchId) => {
    setApproving(true)

    try {
      // Get current user for audit trail
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error: rpcError } = await supabase.rpc('approve_shadow_batch', {
        p_batch_id: batchId,
        p_approved_by: user?.id || null
      })

      if (rpcError) throw rpcError

      toast.success('Batch approved', {
        description: `${data.batch_code} — ${data.tasks_generated} task${data.tasks_generated !== 1 ? 's' : ''} generated`
      })

      return data
    } catch (err) {
      console.error('[usePlanApproval] Approve error:', err)
      toast.error('Failed to approve batch', { description: err.message })
      throw err
    } finally {
      setApproving(false)
    }
  }, [])

  /**
   * Reject a single shadow batch
   *
   * @param {string} batchId - UUID of the shadow batch
   * @returns {Promise<void>}
   */
  const rejectBatch = useCallback(async (batchId) => {
    setApproving(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { error: rpcError } = await supabase.rpc('reject_shadow_batch', {
        p_batch_id: batchId,
        p_rejected_by: user?.id || null
      })

      if (rpcError) throw rpcError

      toast.success('Batch rejected')
    } catch (err) {
      console.error('[usePlanApproval] Reject error:', err)
      toast.error('Failed to reject batch', { description: err.message })
      throw err
    } finally {
      setApproving(false)
    }
  }, [])

  /**
   * Approve multiple shadow batches sequentially
   * Stops on first error and reports partial progress.
   *
   * @param {Array<string>} batchIds - Array of shadow batch UUIDs
   * @returns {Promise<Array<Object>>} Array of approval results
   */
  const bulkApprove = useCallback(async (batchIds) => {
    if (!batchIds?.length) return []

    setApproving(true)
    const results = []

    try {
      const { data: { user } } = await supabase.auth.getUser()

      for (const batchId of batchIds) {
        const { data, error: rpcError } = await supabase.rpc('approve_shadow_batch', {
          p_batch_id: batchId,
          p_approved_by: user?.id || null
        })

        if (rpcError) throw rpcError
        results.push(data)
      }

      const totalTasks = results.reduce((sum, r) => sum + (r.tasks_generated || 0), 0)

      toast.success(`${results.length} batch${results.length !== 1 ? 'es' : ''} approved`, {
        description: `${totalTasks} task${totalTasks !== 1 ? 's' : ''} generated`
      })

      return results
    } catch (err) {
      console.error('[usePlanApproval] Bulk approve error:', err)

      if (results.length > 0) {
        toast.warning(`Partially approved: ${results.length} of ${batchIds.length}`, {
          description: err.message
        })
      } else {
        toast.error('Bulk approve failed', { description: err.message })
      }

      throw err
    } finally {
      setApproving(false)
    }
  }, [])

  return {
    approveBatch,
    rejectBatch,
    bulkApprove,
    approving
  }
}

export default usePlanApproval
